
import { useNodeResize } from "@/hooks/useNodeResize";
import { useNodeEditing } from "@/hooks/useNodeEditing";
import { useNodeEventHandlers } from "./NodeEventHandlers";
import type { FlowNodeData, FlowNodeState } from "./FlowNodeTypes";

interface FlowNodeHooksResult {
  isResizing: boolean;
  handleResizeStart: (e: React.MouseEvent) => void;
  isEditingContent: boolean;
  handleContentClick: () => void;
  handleContentChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleContentBlur: () => void;
  handleCategoryChange: (category: string) => void;
}

export const useFlowNodeHooks = (
  id: string, 
  data: FlowNodeData, 
  state: FlowNodeState
): FlowNodeHooksResult => {
  console.log("useFlowNodeHooks called for node:", id);

  // Initialize resize hook with comprehensive error handling
  let isResizing = false;
  let handleResizeStart = (e: React.MouseEvent) => {
    console.warn("Resize handler not initialized for node:", id);
  };
  
  try {
    const resizeHookResult = useNodeResize({
      id,
      nodeWidth: state.nodeWidth,
      nodeHeight: state.nodeHeight,
      updateNodeData: state.updateNodeData
    });
    
    if (resizeHookResult) {
      isResizing = resizeHookResult.isResizing || false;
      handleResizeStart = resizeHookResult.handleResizeStart || handleResizeStart;
    }
  } catch (error) {
    console.error("Error in useNodeResize hook for node:", id, error);
  }
  
  // Initialize editing hook with comprehensive error handling
  let isEditingContent = false;
  let handleContentClick = () => {
    console.warn("Content click handler not initialized for node:", id);
  };
  let handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    console.warn("Content change handler not initialized for node:", id);
  };
  let handleContentBlur = () => {
    console.warn("Content blur handler not initialized for node:", id);
  };
  
  try {
    const editingHookResult = useNodeEditing({
      id,
      updateNodeData: state.updateNodeData,
      isResizing,
      isClientMode: Boolean(data.isClientMode),
      originalContent: data.originalContent,
      clientEdits: data.clientEdits
    });
    
    if (editingHookResult) {
      isEditingContent = editingHookResult.isEditingContent || false;
      handleContentClick = editingHookResult.handleContentClick || handleContentClick;
      handleContentChange = editingHookResult.handleContentChange || handleContentChange;
      handleContentBlur = editingHookResult.handleContentBlur || handleContentBlur;
    }
  } catch (error) {
    console.error("Error in useNodeEditing hook for node:", id, error);
  }

  // Initialize event handlers hook with comprehensive error handling
  let handleCategoryChange = (category: string) => {
    console.warn("Category change handler not initialized for node:", id);
  };
  
  try {
    const eventHandlersResult = useNodeEventHandlers({
      id,
      updateNodeData: state.updateNodeData,
      onNodeSelect: data.onNodeSelect,
      onNodeDelete: data.onNodeDelete,
      isClientMode: Boolean(data.isClientMode)
    });
    
    if (eventHandlersResult) {
      handleCategoryChange = eventHandlersResult.handleCategoryChange || handleCategoryChange;
    }
  } catch (error) {
    console.error("Error in useNodeEventHandlers hook for node:", id, error);
  }

  return {
    isResizing,
    handleResizeStart,
    isEditingContent,
    handleContentClick,
    handleContentChange,
    handleContentBlur,
    handleCategoryChange
  };
};
